import { useEffect, useMemo, useState } from "react";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useScriptMode } from "../../../../contexts/ScriptModeContext";
import { useGetGroupProgressQuery } from "../../../../api/groupsApi";
import { PERIOD_OPTIONS, periodRange, toLocalDate } from "../../../../lib/periodRange";
import {
  compareLabel,
  groupCompare,
  memberDelta,
  memberRate,
  previousPeriodRange,
} from "../../../../lib/progressCompare";
import PeriodSelect from "./PeriodSelect";
import MemberGoalBreakdown from "./MemberGoalBreakdown";
import GoalSelect from "./GoalSelect";
import { commonGoalOptions, filterByGoal, formatAmount } from "./goalFilter";
import InsightsSkeleton from "./InsightsSkeleton";
import { formatDelta } from "./insightsFormat";
import { deltaClass, soft } from "./softClasses";
import { GroupMemberDialog } from "../../../titles/MemberTitles";
import UserAvatar from "../../../../components/ui/UserAvatar";

/**
 * İstatistik (üye) ve sıralama: dönem ve grup ortalaması üstte sabit,
 * altında tek katmanlı satırlar (sıra, ad ve ünvan, ince çubuk, oran; altında
 * mükemmel gün, seri, odak). Oranın yanında bir önceki eşit döneme göre fark.
 * Kendi satırın hafif vurgulu. Satıra basınca o kardeşin künyesi ve
 * ünvanları açılır (mobil GroupMemberModal). Grup izin veriyorsa (canSeeGoals)
 * satırın başındaki okla kardeşin hedef hedef ilerlemesi (members[i].goals)
 * satırın altında açılır; aynı anda birden çok satır açık kalabilir. Yine
 * canSeeGoals açıkken dönemin yanında ortak hedef seçimi çıkar: seçilince
 * yalnız o hedefi olan kardeşler, yapılan miktara göre sıralanır, satırda
 * oran yerine miktar ("15 sayfa"), üstte ortalama yerine toplam görünür.
 */
export default function GroupRankingView({ groupId }) {
  const { t } = useScriptMode();
  const [period, setPeriod] = useState("today");
  const [openMemberId, setOpenMemberId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [goalFilter, setGoalFilter] = useState(null);
  const range = periodRange(period);
  const prev = previousPeriodRange(period, range.start, range.end);

  const { data, isFetching } = useGetGroupProgressQuery(
    { groupId, startDate: range.startDate, endDate: range.endDate },
    { skip: !groupId }
  );
  // Yeni sunucu farkları kendisi veriyor (averageDelta, members[].delta);
  // önceki dönem sorgusu yalnız eski sunucuda atılır.
  const serverCompare = data?.data ? data.data.averageDelta !== undefined : false;
  const { data: prevData } = useGetGroupProgressQuery(
    { groupId, startDate: toLocalDate(prev.start), endDate: toLocalDate(prev.end) },
    { skip: !groupId || !data?.data || isFetching || serverCompare }
  );

  const members = useMemo(() => data?.data?.members || [], [data]);
  const canSeeGoals = Boolean(data?.data?.canSeeGoals);
  const goalOptions = useMemo(() => (canSeeGoals ? commonGoalOptions(members) : []), [canSeeGoals, members]);
  const selectedGoal = goalOptions.find((o) => o.key === goalFilter) || null;
  const filtered = useMemo(
    () => (selectedGoal ? filterByGoal(members, selectedGoal.key) : null),
    [selectedGoal, members]
  );
  const rows = filtered ? filtered.members : members;
  // Seçicilerin yanındaki yumuşak yazı: "Bu hafta" ya da "Bu hafta · Risale".
  const periodLabel = t((PERIOD_OPTIONS.find((o) => o.key === period) || PERIOD_OPTIONS[0]).label);
  const selectionText = selectedGoal ? `${periodLabel} · ${selectedGoal.label}` : periodLabel;

  // Grup değişince süzgeç sıfırlanır; dönem değişip hedef seçeneklerden
  // düşerse "Tüm hedefler"e dönülür.
  useEffect(() => setGoalFilter(null), [groupId]);
  useEffect(() => {
    if (goalFilter && !isFetching && !goalOptions.some((o) => o.key === goalFilter)) setGoalFilter(null);
  }, [goalFilter, goalOptions, isFetching]);
  const toggleExpanded = (id) =>
    setExpanded((prevSet) => {
      const next = new Set(prevSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const prevMembers = serverCompare ? null : prevData?.data?.members;
  const prevById = useMemo(() => {
    const map = new Map();
    (prevMembers || []).forEach((m) => map.set(String(m.id), memberRate(m)));
    return map;
  }, [prevMembers]);
  const { average, averageDelta } = groupCompare({
    average: data?.data?.average,
    averageDelta: data?.data?.averageDelta,
    members,
    previousMembers: prevMembers,
  });

  if (isFetching && !members.length) return <InsightsSkeleton layout="ranking" rows={8} />;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Üstte sabit: dönem ve grup ortalaması */}
      <div className={`${soft.summary} flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-2 min-w-0">
          <PeriodSelect value={period} onChange={setPeriod} disabled={isFetching} variant="icon" />
          {canSeeGoals && goalOptions.length ? (
            <GoalSelect
              options={goalOptions}
              value={goalFilter}
              onChange={setGoalFilter}
              disabled={isFetching}
              variant="icon"
            />
          ) : null}
          <span data-ranking-selection className={`ml-1 text-sm truncate ${soft.muted}`}>
            {selectionText}
          </span>
        </div>
        {filtered ? (
          <div className="flex flex-col items-end shrink-0">
            <span className={`text-[11px] ${soft.muted}`}>{t("Toplam")}</span>
            <span className={`text-2xl font-light ${soft.accent}`}>
              {formatAmount(filtered.total, selectedGoal.unit, t)}
            </span>
          </div>
        ) : members.length ? (
          <div className="flex flex-col items-end">
            <span className={`text-[11px] ${soft.muted}`}>{t("Grup ortalaması")}</span>
            <span className="flex items-baseline gap-1.5">
              {averageDelta !== null ? (
                <span className={`text-xs font-medium ${deltaClass(averageDelta)}`}>
                  {formatDelta(averageDelta)}
                </span>
              ) : null}
              <span className={`text-2xl font-light ${soft.accent}`}>%{average}</span>
            </span>
          </div>
        ) : null}
      </div>

      {isFetching ? (
        <InsightsSkeleton layout="rows" rows={6} />
      ) : members.length === 0 ? (
        <div className={soft.empty}>
          <PeopleOutlineIcon aria-hidden />
          {t("Grupta kimse yok")}
        </div>
      ) : (
        <ul className="flex-1 min-h-0 overflow-y-auto">
          {rows.map((m) => {
            const rate = filtered ? m.barRate : memberRate(m);
            const delta = memberDelta(m, prevById.get(String(m.id)));
            const isOpen = canSeeGoals && expanded.has(String(m.id));
            const meta = [
              m.perfectDays > 0 ? `${m.perfectDays} ${t("mükemmel gün")}` : null,
              m.streak > 0 ? `${m.streak} ${t("gün seri")}` : null,
              m.focusMinutes > 0 ? `${m.focusMinutes} ${t("dk")}` : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={m.id} data-member={m.id} className="relative">
                {canSeeGoals ? (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(String(m.id))}
                    aria-expanded={isOpen}
                    aria-label={t(isOpen ? "Hedefleri gizle" : "Hedefleri göster")}
                    className={`absolute left-2 top-3 z-10 w-8 h-8 flex items-center justify-center rounded-full hover:bg-terakkiBrandMist dark:hover:bg-white/10 transition-colors ${soft.muted}`}
                  >
                    {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setOpenMemberId(String(m.id))}
                  className={`${soft.rowButton} ${canSeeGoals ? "pl-11" : ""} ${
                    m.isMe ? "bg-terakkiBrandCloud dark:bg-white/[0.08]" : ""
                  }`}
                >
                  <span
                    className={`w-6 text-center text-sm ${
                      m.rank <= 3 ? `font-medium ${soft.accent}` : soft.muted
                    }`}
                  >
                    {m.rank}
                  </span>
                  <UserAvatar person={m} size={36} />
                  <span className="flex-1 min-w-0 flex flex-col gap-1">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`text-[15px] font-medium truncate ${soft.ink}`}>
                        {m.name} {m.surname}
                      </span>
                    </span>
                    <span className={soft.track}>
                      <span
                        data-bar
                        className={`block h-full rounded-full ${
                          m.isMe ? "bg-terakkiPrimaryDark dark:bg-white" : "bg-terakkiBrand dark:bg-terakkiBrandSoft"
                        }`}
                        style={{ width: `${rate}%` }}
                      />
                    </span>
                    {meta ? <span className={`text-[11px] truncate ${soft.faint}`}>{meta}</span> : null}
                  </span>
                  <span className="flex flex-col items-end min-w-[52px]">
                    {filtered ? (
                      <span className={`text-[15px] font-medium tabular-nums ${soft.accent}`}>
                        {formatAmount(m.goalValue, m.goalUnit, t)}
                      </span>
                    ) : (
                      <span className={`text-[15px] font-medium ${soft.accent}`}>%{memberRate(m)}</span>
                    )}
                    {!filtered && delta !== null ? (
                      <span
                        className={`text-[11px] ${deltaClass(delta)}`}
                        title={t(compareLabel(period))}
                      >
                        {formatDelta(delta)}
                      </span>
                    ) : null}
                  </span>
                </button>
                {isOpen ? (
                  <MemberGoalBreakdown goals={m.goals} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {members.length && !filtered ? (
        <p className={`shrink-0 px-5 py-2 text-[11px] border-t border-terakkiBorder dark:border-white/15 ${soft.faint}`}>
          {`${t("Fark")}: ${t(compareLabel(period))}`}
        </p>
      ) : null}
      <GroupMemberDialog
        groupId={groupId}
        memberId={openMemberId}
        range={{ startDate: range.startDate, endDate: range.endDate }}
        onClose={() => setOpenMemberId(null)}
      />
    </div>
  );
}
